import { useEffect, useMemo } from 'react';
import './staySection.css';

// Optional "Stay" details for a trip — short description, name, location and
// up to 5 images. Used by both the organizer and support trip forms. Images
// are kept as a mix of existing URL strings (on edit) and new File objects;
// the submit handler uploads the Files to Firebase before saving.
const MAX_IMAGES = 5;

export default function StaySection({ packData, setPackData, isEdit, disabled = false }) {
    const stay = packData.stayDetails || { name: '', location: '', mapLink: '', description: '', images: [] };
    const images = stay.images || [];

    // make sure stayDetails always exists on the form object
    useEffect(() => {
        if (!packData.stayDetails) {
            setPackData((prev) => ({
                ...prev,
                stayDetails: { name: '', location: '', mapLink: '', description: '', images: [] }
            }));
        }
        // eslint-disable-next-line
    }, []);

    const setField = (field, value) =>
        setPackData((prev) => ({
            ...prev,
            stayDetails: { ...(prev.stayDetails || {}), [field]: value }
        }));

    const setImages = (next) =>
        setPackData((prev) => ({
            ...prev,
            stayDetails: { ...(prev.stayDetails || {}), images: next }
        }));

    const addFiles = (fileList) => {
        const files = Array.from(fileList || []);
        if (!files.length) return;
        const room = MAX_IMAGES - images.length;
        setImages([...images, ...files.slice(0, room)]);
    };

    const removeImage = (index) => setImages(images.filter((_, i) => i !== index));

    // preview URLs (revoke object URLs on unmount)
    const previews = useMemo(
        () => images.map((img) => (typeof img === 'string' ? img : URL.createObjectURL(img))),
        [images]
    );
    useEffect(() => {
        return () => {
            previews.forEach((url, i) => {
                if (typeof images[i] !== 'string') URL.revokeObjectURL(url);
            });
        };
        // eslint-disable-next-line
    }, [previews]);

    return (
        <div className='staySection'>
            <h2 className='orgPackUpH2'>
                Stay <span className='stayOptional'>(optional)</span>
            </h2>

            <div className='orgPackUpSecDiv stayCard'>
                <p className='stayHint'>
                    Add where guests will stay. Everything here is optional. Stay photos appear in the trip gallery
                    after the first 2 images, and under a “Stay” tab on the trip page.
                </p>

                <div className='stayGrid'>
                <div className='stayField'>
                    <label className='stayLabel'>Stay name</label>
                    <input
                        className='stayInput'
                        value={stay.name || ''}
                        disabled={disabled}
                        placeholder='e.g. Coorg Forest Resort'
                        maxLength={120}
                        onChange={(e) => setField('name', e.target.value)}
                    />
                </div>

                <div className='stayField'>
                    <label className='stayLabel'>Location</label>
                    <input
                        className='stayInput'
                        value={stay.location || ''}
                        disabled={disabled}
                        placeholder='e.g. Madikeri, Coorg'
                        maxLength={160}
                        onChange={(e) => setField('location', e.target.value)}
                    />
                </div>
            </div>

            <div className='stayField'>
                <label className='stayLabel'>
                    Google Maps link <span className='stayCount'>optional</span>
                </label>
                <input
                    className='stayInput'
                    type='url'
                    value={stay.mapLink || ''}
                    disabled={disabled}
                    placeholder='https://maps.app.goo.gl/…'
                    onChange={(e) => setField('mapLink', e.target.value)}
                />
            </div>

            <div className='stayField'>
                <label className='stayLabel'>Short description</label>
                <textarea
                    className='stayInput stayTextarea'
                    rows={3}
                    value={stay.description || ''}
                    disabled={disabled}
                    placeholder='A line or two about the stay — rooms, view, meals…'
                    maxLength={500}
                    onChange={(e) => setField('description', e.target.value)}
                />
            </div>

            <div className='stayField'>
                <label className='stayLabel'>
                    Stay images <span className='stayCount'>{images.length}/{MAX_IMAGES}</span>
                </label>

                <div className='stayImages'>
                    {previews.map((url, i) => (
                        <div className='stayImageTile' key={i}>
                            <img src={url} alt={`Stay ${i + 1}`} />
                            {!disabled && (
                                <button type='button' className='stayImageRemove' onClick={() => removeImage(i)}>
                                    ✕
                                </button>
                            )}
                        </div>
                    ))}

                    {!disabled && images.length < MAX_IMAGES && (
                        <label className='stayImageAdd'>
                            <span>+</span>
                            <small>Add image</small>
                            <input
                                type='file'
                                accept='image/*'
                                multiple
                                hidden
                                onChange={(e) => {
                                    addFiles(e.target.files);
                                    e.target.value = '';
                                }}
                            />
                        </label>
                    )}
                </div>
            </div>
            </div>
        </div>
    );
}
